/**
 * ConnectionStatusToast — Wave 1 (Bill 2026-04-28).
 *
 * Subtle bottom-right toast that surfaces when the api-client transparently
 * retries a transient 5xx (the cold-start jXchange auth-failed signature).
 * Listens for the `pcb:api-retry` window event fired by createApiClient's
 * `onRetry` hook, shows "Reconnecting to Core Director… give it a moment",
 * and auto-dismisses 6s after the most recent retry event (or sooner if a
 * `pcb:api-retry-clear` event lands).
 *
 * UX rationale (Bill): the FSR shouldn't see "Azure Function" or "warming
 * up" jargon. "Core Director" is the term they already use day-to-day. The
 * banner is non-modal so the FSR can keep typing — they just learn the
 * delay is a transient connection thing, not their input being lost.
 */
import { useEffect, useState } from 'react';

const RETRY_EVENT = 'pcb:api-retry';
const RETRY_CLEAR_EVENT = 'pcb:api-retry-clear';
const AUTO_DISMISS_MS = 6000;

export function ConnectionStatusToast(): JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let dismissTimer: number | null = null;

    const onRetry = (): void => {
      setVisible(true);
      if (dismissTimer !== null) window.clearTimeout(dismissTimer);
      dismissTimer = window.setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    };

    const onClear = (): void => {
      if (dismissTimer !== null) window.clearTimeout(dismissTimer);
      setVisible(false);
    };

    window.addEventListener(RETRY_EVENT, onRetry);
    window.addEventListener(RETRY_CLEAR_EVENT, onClear);

    return () => {
      window.removeEventListener(RETRY_EVENT, onRetry);
      window.removeEventListener(RETRY_CLEAR_EVENT, onClear);
      if (dismissTimer !== null) window.clearTimeout(dismissTimer);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        background: 'var(--surface-card, #fff)',
        color: 'var(--ink-700, #2c3e50)',
        border: '1px solid var(--ink-200, #dfe3e8)',
        boxShadow: 'var(--sh-2, 0 4px 12px rgba(0,0,0,0.08))',
        borderRadius: 'var(--r-md, 8px)',
        padding: '10px 14px',
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        zIndex: 1500,
        maxWidth: 360,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          border: '2px solid var(--pcb-blue)',
          borderTopColor: 'transparent',
          animation: 'pcb-conn-spin 0.9s linear infinite',
          flexShrink: 0,
        }}
      />
      <span>
        <strong>Reconnecting to Core Director…</strong> give it a moment
      </span>
      <style>{`
        @keyframes pcb-conn-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
